// front-operational/src/context/RolesContext.js
// Context pour gérer les rôles et permissions de manière dynamique

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getRoles } from '../api/api';
import { useWilout } from './WiloutContext';

const RolesContext = createContext(null);

// Permissions par défaut (fallback si les rôles ne sont pas chargés)
const DEFAULT_PERMISSIONS = {
  isAdmin: false,
  canManageUsers: false,
  canManageRoles: false,
  canEditEvent: false,
  canEditChecklist: false,
  canAccessFiles: false,
  canDeleteEvents: false,
  canAccessAdmin: false,
  canAccessOnboarding: false,
  canEditOnboarding: false,
  canAccessTerminals: false,
  canEditTerminals: false,
  canAccessMdmSunmi: false,
  canEditMdmSunmi: false,
  canAccessInvoices: false,
  canEditInvoices: false,
  canViewResources: false,
  canManageResources: false,
  canViewInfosCles: false,
  canEditInfosCles: false,
  canViewEvents: false,
  canEditFiles: false,
  canAccessRetex: false,
  canEditRetex: false,
  canAccessOrderSearch: false,
  canAccessSchedule: false,
  canEditSchedule: false,
  canValidateSchedule: false,
  canViewAllSchedules: false,
};

export const RolesProvider = ({ children }) => {
  const { user } = useWilout();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // true dès le premier chargement terminé (succès OU échec définitif) : l'app
  // attend ce signal avant d'afficher les pages, sinon toutes les permissions
  // valent false le temps du chargement (flash "Accès refusé", routes absentes).
  const [ready, setReady] = useState(false);
  const lastLoadedAt = useRef(0);

  // Charger les rôles depuis l'API (avec retry)
  const loadRoles = useCallback(async (retryCount = 0) => {
    setLoading(true);
    try {
      const data = await getRoles();
      setRoles(data);
      setError(null);
      lastLoadedAt.current = Date.now();
    } catch (err) {
      console.error('[RolesContext] Erreur chargement des rôles:', err);
      // Retry une fois après 1s (token Auth0 peut ne pas être prêt immédiatement).
      // Pas de finally : il remettait loading à false pendant l'attente du retry.
      if (retryCount < 1) {
        setTimeout(() => loadRoles(retryCount + 1), 1000);
        return;
      }
      setError(err.message);
    }
    setLoading(false);
    setReady(true);
  }, []);

  // Charger les rôles uniquement quand l'utilisateur est connecté
  useEffect(() => {
    if (user) {
      loadRoles();
    } else {
      setRoles([]);
      setLoading(false);
      setReady(false);
    }
  }, [user, loadRoles]);

  // Relire les rôles au retour sur l'onglet (au plus une fois par minute) : une
  // modification faite dans Admin > Rôles s'applique sans recharger la page.
  useEffect(() => {
    if (!user) return undefined;
    const onFocus = () => {
      if (Date.now() - lastLoadedAt.current > 60 * 1000) loadRoles();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, loadRoles]);

  // Récupérer un rôle par son nom
  const getRoleByName = useCallback((roleName) => {
    if (!roleName) return null;
    const found = roles.find(r => r.name === roleName.toUpperCase());
    if (!found && roles.length > 0) {
      console.warn(`[RolesContext] Rôle "${roleName}" introuvable parmi les ${roles.length} rôles chargés. Permissions vides.`);
    }
    return found;
  }, [roles]);

  // Récupérer les permissions d'un rôle
  const getRolePermissions = useCallback((roleName) => {
    const role = getRoleByName(roleName);
    if (!role) return DEFAULT_PERMISSIONS;
    return { ...DEFAULT_PERMISSIONS, ...role.permissions };
  }, [getRoleByName]);

  // Vérifier si un utilisateur a une permission spécifique
  const hasPermission = useCallback((roleName, permission, context = {}) => {
    const role = getRoleByName(roleName);
    if (!role || !role.permissions) return false;

    const permValue = role.permissions[permission];

    // Permission boolean simple
    if (typeof permValue === 'boolean') {
      return permValue;
    }

    // Permission conditionnelle
    if (typeof permValue === 'string' && permValue.startsWith('conditional:')) {
      const condition = permValue.replace('conditional:', '');

      switch (condition) {
        case 'assigned':
          // Vérifier si l'utilisateur est assigné à l'événement
          if (!context.userEmail || !context.eventChefEmail) return false;
          return context.userEmail.toLowerCase() === context.eventChefEmail.toLowerCase();

        default:
          return false;
      }
    }

    return false;
  }, [getRoleByName]);

  // Vérifier si un utilisateur peut accéder à une fonctionnalité
  const canAccess = useCallback((roleName, feature) => {
    const permissionMap = {
      admin: 'canAccessAdmin',
      users: 'canManageUsers',
      roles: 'canManageRoles',
      terminals: 'canAccessTerminals',
      invoices: 'canAccessInvoices',
      onboarding: 'canAccessOnboarding',
      files: 'canAccessFiles',
      events: 'canViewEvents',
      resources: 'canViewResources',
      infosCles: 'canViewInfosCles',
      retex: 'canAccessRetex',
    };

    const permission = permissionMap[feature];
    if (!permission) return false;

    return hasPermission(roleName, permission);
  }, [hasPermission]);

  // Vérifier si un rôle est un admin (ADMIN ou ADMIN MAROC)
  const isAdminRole = useCallback((roleName) => {
    const role = getRoleByName(roleName);
    return role?.permissions?.isAdmin === true;
  }, [getRoleByName]);

  // Récupérer les emails autorisés pour un rôle (ex: ADMIN MAROC)
  const getAllowedEmails = useCallback((roleName) => {
    const role = getRoleByName(roleName);
    return role?.conditionalRules?.allowedChefEmails || [];
  }, [getRoleByName]);

  // Vérifier si un rôle nécessite l'assignation à l'événement
  const requiresEventAssignment = useCallback((roleName) => {
    const role = getRoleByName(roleName);
    return role?.conditionalRules?.requiresEventAssignment === true;
  }, [getRoleByName]);

  // Vérifier si un rôle est team-scoped
  const isTeamScopedRole = useCallback((roleName) => {
    const role = getRoleByName(roleName);
    return role?.conditionalRules?.teamScoped === true;
  }, [getRoleByName]);

  // Liste des noms de rôles (pour les dropdowns)
  const roleNames = useMemo(() => roles.map(r => r.name), [roles]);

  const value = {
    roles,
    ready,
    loading,
    error,
    loadRoles,
    getRoleByName,
    getRolePermissions,
    hasPermission,
    canAccess,
    isAdminRole,
    getAllowedEmails,
    requiresEventAssignment,
    isTeamScopedRole,
    roleNames,
    DEFAULT_PERMISSIONS,
  };

  return (
    <RolesContext.Provider value={value}>
      {children}
    </RolesContext.Provider>
  );
};

export const useRoles = () => {
  const context = useContext(RolesContext);
  if (!context) {
    throw new Error('useRoles must be used within a RolesProvider');
  }
  return context;
};

export default RolesContext;
